<template>
  <Grid>
    <Col12>
      <h3 class="text-sm font-semibold text-[var(--color-text-primary)]">{{ tenderHeader }}</h3>
      <Divider class="mt-1" />
    </Col12>
  </Grid>

  <FieldArrayValidator :name="`${formNamespace}tenders`" :key="dummyState">
    <template v-slot="{ fields, remove, push, error }">
      <Grid class="grid-gutter-md">
        <template v-for="(field, index) in fields" :key="field.key">
          <!-- Mean Plan Tenders -->
          <template v-if="isITC">
            <ITCTender
              :index="index"
              :tender-id-field="fieldID(index)"
              :tender-type-field="fieldType(index)"
              :plan-type="mealPlanType"
              :isDisabled="isDisabled"
            />
          </template>

          <template v-else-if="isAtrium">
            <AtriumTender
              :index="index"
              :tender-id-field="fieldID(index)"
              :tender-type-field="fieldType(index)"
              :tender-name-field="fieldName(index)"
              :field="field"
              :plan-type="mealPlanType"
              :isDisabled="isDisabled"
            />
          </template>

          <template v-else-if="isTransact">
            <TransactTender
              :index="index"
              :tender-id-field="fieldID(index)"
              :tender-type-field="fieldType(index)"
              :tender-name-field="fieldName(index)"
              :field="field"
              :plan-type="mealPlanType"
              :isDisabled="isDisabled"
            />
          </template>

          <template v-else-if="isCBORD">
            <CBORDTender
              :index="index"
              :tender-id-field="fieldID(index)"
              :tender-type-field="fieldType(index)"
              :tender-name-field="fieldName(index)"
              :field="field"
              :plan-type="mealPlanType"
              :isDisabled="isDisabled"
            />
          </template>

          <!-- Cashless Pay Tenders -->
          <template v-else-if="isFreedomPay">
            <FreedomPayTender
              :index="index"
              :tender-type-field="fieldType(index)"
              :tender-name-field="fieldName(index)"
              :plan-type="mealPlanType"
              :isDisabled="isDisabled"
            />
          </template>

          <template v-if="!isEmpty(mealPlanType)">
            <Col2 v-if="showTenderTaxExempt(field)">
              <div class="flex justify-center">
                <div>
                  <TenderTaxExempt
                    :fieldName="fieldTax(index)"
                    :index="index"
                    :plan-type="mealPlanType"
                    :isDisabled="isDisabled"
                  />
                </div>
              </div>
            </Col2>

            <Col2
              :class="{
                'col-offset-2': !showTenderTaxExempt(field),
              }"
            >
              <div class="flex items-center mt-4" v-if="!isDisabled">
                <Transition name="fade">
                  <IconButton
                    :id="`delete-tender-${mealPlanType}-${index}`"
                    class="c-icon-button mr-4"
                    @click="removeTender(index, remove)"
                    v-if="fields.length > 1"
                  >
                    <template #icon>
                      <i class="material-symbols-rounded text-[1.25rem]"> delete </i>
                    </template>
                  </IconButton>
                </Transition>

                <Transition name="fade">
                  <IconButton
                    :id="`add-tender-${mealPlanType}-${index}`"
                    class="c-icon-button"
                    @click="push(createNewTender())"
                  >
                    <template #icon>
                      <i class="material-symbols-rounded text-[1.25rem]"> add </i>
                    </template>
                  </IconButton>
                </Transition>
              </div>
            </Col2>
          </template>
        </template>
      </Grid>
    </template>
  </FieldArrayValidator>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, inject, ref } from 'vue';
import TenderTaxExempt from './tender-tax-exempt/TenderTaxExempt.vue';
import { type FieldEntry } from 'vee-validate';
import {
  ATRIUM_TYPES,
  ITC_TYPES,
  TENDER_TYPES,
  TRANSACT_TYPES,
  CBORD_TYPES,
  FREEDOM_PAY_TYPES,
} from '../enums';
import ITCTender from './itc-tender/ITCTender.vue';
import AtriumTender from './atrium-tender/AtriumTender.vue';
import TransactTender from './transact-tender/TransactTender.vue';
import type { Tender } from '@centric-os/types';
import CBORDTender from './cbord-tender/CBORDTender.vue';
import FreedomPayTender from './freedom-pay-tender/FreedomPayTender.vue';
import { isEmpty } from 'lodash-es';

// Define Props
const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false,
  },
  mealPlanType: {
    type: String,
    required: true,
  },
});

// Set localization
const { t } = useI18n();

// Create a dummy reactive state
const dummyState = ref<number>(0);

const formNamespace = inject('formNamespace', '');

// Set up field names
const fieldID = (index: number): string => `${formNamespace}tenders[${index}].id`;
const fieldTax = (index: number): string => `${formNamespace}tenders[${index}].tax_exempt`;
const fieldType = (index: number): string => `${formNamespace}tenders[${index}].type`;
const fieldName = (index: number): string => `${formNamespace}tenders[${index}].name`;

const isITC = computed<boolean>(() => ITC_TYPES.includes(props.mealPlanType));
const isAtrium = computed<boolean>(() => ATRIUM_TYPES.includes(props.mealPlanType));
const isTransact = computed<boolean>(() => TRANSACT_TYPES.includes(props.mealPlanType));
const isCBORD = computed<boolean>(() => CBORD_TYPES.includes(props.mealPlanType));
const isFreedomPay = computed<boolean>(() => FREEDOM_PAY_TYPES.includes(props.mealPlanType));

// Method to hide tender tax exempt field
const showTenderTaxExempt = (field: FieldEntry<Tender>): boolean => {
  return field.value?.type !== TENDER_TYPES.MEAL_EXCHANGE;
};

// Remove tender
const removeTender = (index: number, remove: (index: number) => void): void => {
  remove(index);
  dummyState.value++;
};

// Method to add new tender
const createNewTender = (): Partial<Tender> => {
  return {
    id: '',
    name: '',
    // @ts-expect-error FIXME: Type '""' is not assignable to type 'TenderTypes'.
    type: '',
    tax_exempt: false,
  };
};

// Computed to check if tender is cashless pay
const isCashlessPay = computed<boolean>(() => {
  return !isEmpty(props.mealPlanType) && FREEDOM_PAY_TYPES.includes(props.mealPlanType);
});

// Computed to check if tender is meal plan
const isMealPlan = computed<boolean>(() => {
  return !isEmpty(props.mealPlanType) && !FREEDOM_PAY_TYPES.includes(props.mealPlanType);
});

// Computed for tender header
const tenderHeader = computed<string>(() => {
  if (isMealPlan.value) {
    return t('sites.singleSite.financialConfig.mealPlans.form.tenderHeader');
  } else if (isCashlessPay.value) {
    return t('sites.singleSite.financialConfig.cashlessPay.form.tenderHeader');
  } else {
    return '';
  }
});
</script>
